// src/App.tsx
import React from 'react';
import { useTeacher } from './context/TeacherContext';
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Box, CircularProgress, ThemeProvider, CssBaseline } from '@mui/material';
import TopNavigation from './Components/Navigation/TopNavigation';
import appTheme from './theme/appTheme';
import Login from './Components/Login';
import Register from './Components/Register';
import Dashboard from './Components/Dashboard';
import { GuidedReading } from './Components/GuidedReading';
import Settings from './Components/Settings/Settings';
import Students from './Components/Student/Students';
import StudentList from './Components/Student/StudentList';
import { Intervention } from './Components/Intervention';
import GroupList from './Components/Groups/GroupList';
import PrivateRoute from './Components/Auth/PrivateRoute';
import Passages from './Components/Resources/Passages';
import ShortPassages from './Components/Resources/ShortPassages';
import Worksheets from './Components/Resources/Worksheets';
import AddStudents from './Components/Students/AddStudents';
import AIChat from './Components/AIChat/AIChat';
import TeachingChat from './Components/TeachingChat/TeachingChat';
import Home from './Components/Home';
// import LessonPlan from './Components/SmallGroup/LessonPlan'; // COMMENTED OUT DUE TO ERRORS
import SmallGroupLessonPlan from './Components/SmallGroup/SmallGroupLessonPlan'; // NEW: Using this instead
import SavedLessonPlans from './Components/SmallGroup/SavedLessonPlans';
import LessonDelivery from './Components/SmallGroup/LessonDelivery';
import DeliveredLessons from './Components/SmallGroup/DeliveredLessons';
import Signup from './Components/Signup';
import CompleteProfile from './Components/CompleteProfile';
import AuthCallback from './Components/Auth/AuthCallback';
import Profile from './Components/Profile/Profile';
import Subscription from './Components/Subscription/Subscription';
import SubscriptionSuccess from './Components/Subscription/SubscriptionSuccess';
import SubscriptionFailure from './Components/Subscription/SubscriptionFailure';
import Assessments from './Components/Resources/Assessments';
import MathWordProblems from './Components/Resources/MathWordProblems';
import MathWordProblemsList from './Components/Resources/MathWordProblemsList';
import Tools from './Components/Resources/Tools';
import PassagesList from './Components/Resources/PassagesList';
import TestBuilder from './Components/Tests/TestBuilder';
import GradeTests from './Components/Tests/GradeTests';
import TakeTest from './Components/Tests/TakeTest';
import GradeSubmissions from './Components/Tests/GradeSubmissions';
import Gradebook from './Components/Gradebook/Gradebook';
import InteriorPageLayout from './Components/Layout/InteriorPageLayout';
import { BehaviorPageEnhanced } from './Components/Behavior';
import { InstructionPage } from './Components/Instruction';
import PrivacyPolicy from './Components/PrivacyPolicy';
import TermsOfService from './Components/TermsOfService';
import Contact from './Components/Contact';
import TeacherStats from './Components/Admin/TeacherStats';
import StandardsVisualization from './Components/Standards/StandardsVisualization';
import { Landing } from './Components/Homeschool/Landing';
import { OnboardingWizard } from './Components/Homeschool/OnboardingWizard';
import MasteryMap from './Components/Homeschool/MasteryMap';
import PlacementFlow from './Components/Homeschool/PlacementFlow';
import SkillDetail from './Components/Homeschool/SkillDetail';
import SkillTree from './Components/Homeschool/SkillTree';
import AssessmentsHub from './Components/Homeschool/AssessmentsHub';
import WeeklyProgress from './Components/Homeschool/WeeklyProgress';
import { HOMESCHOOL_PRIMARY } from './config/features';

/**
 * App component
 *
 * Main application component that handles routing and global layout.
 * Interior pages are now wrapped with InteriorPageLayout for consistent navigation.
 */
function App() {
  const { teacher, loading } = useTeacher();
  const location = useLocation();

  if (loading) {
    return (
      <ThemeProvider theme={appTheme}>
        <CssBaseline />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
        >
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  // Define public routes
  const publicRoutes = [
    '/',
    '/login',
    '/register',
    '/signup',
    '/auth/callback',
    '/privacy-policy',
    '/terms-of-service',
    '/contact',
    '/homeschool'
  ];
  const isAssignmentPublicRoute = location.pathname.startsWith('/a/');
  const isTestPublicRoute = location.pathname.startsWith('/test/');
  const isPublicRoute = publicRoutes.includes(location.pathname) || isAssignmentPublicRoute || isTestPublicRoute;
  const showTopBar = !!teacher && !isAssignmentPublicRoute && !isTestPublicRoute;

  // If user is not logged in and trying to access a protected route, redirect to login
  if (!teacher && !isPublicRoute) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If user is logged in and trying to access public routes (including home), redirect
  // based on role and flag state:
  // - flag off → /edu-chat (teacher primary, old behavior)
  // - flag on + parent → /dashboard (homeschool primary)
  // - flag on + teacher → /teacher/edu-chat
  // But allow test/assignment public routes to pass through
  if (teacher && isPublicRoute && !isAssignmentPublicRoute && !isTestPublicRoute) {
    const role = teacher.role || 'teacher';
    const dest = HOMESCHOOL_PRIMARY && role === 'parent'
      ? '/dashboard'
      : (HOMESCHOOL_PRIMARY && role === 'teacher' ? '/teacher/edu-chat' : '/edu-chat');
    return <Navigate to={dest} replace />;
  }

  const isParent = teacher?.role === 'parent';

  // Helper function to wrap components with InteriorPageLayout
  // Dashboard gets special treatment and doesn't use the layout
  const withInteriorLayout = (Component: React.ComponentType<any>) => (props: any) => {
    if (location.pathname === '/dashboard') {
      return <Component {...props} />;
    }
    return (
      <InteriorPageLayout>
        <Component {...props} />
      </InteriorPageLayout>
    );
  };

  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
      }}>
        {showTopBar && <TopNavigation />}
        <Box sx={{ display: 'flex', flexGrow: 1, minHeight: 0 }}>
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              minWidth: 0,
              backgroundColor: 'background.default',
              minHeight: '100vh',
              mt: showTopBar ? '50px' : 0,
            }}
          >
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={!teacher && HOMESCHOOL_PRIMARY ? <Landing /> : <Home />} />
            {/* Always-reachable entry point to the parent/homeschool landing
                page, independent of HOMESCHOOL_PRIMARY - lets the homeschool
                signup flow be tested/used by direct link while that flag
                stays off and the default teacher experience is unchanged. */}
            <Route path="/homeschool" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/contact" element={<Contact />} />
            {/* Public Assignment Link Route */}
            <Route path="/a/:publicId" element={<TakeTest />} />

            {/* Onboarding wizard - harmless to register unconditionally; only
                reachable by a parent-role account navigating there. */}
            <Route path="/onboarding" element={
              <PrivateRoute>
                <OnboardingWizard />
              </PrivateRoute>
            } />

            {/*
              /dashboard branches on role rather than on the flag: a parent
              account only exists after an explicit parent-role signup, so
              this never changes what an existing teacher sees (flag-off
              behavior stays byte-identical for teachers - the plan's
              requirement). /teacher/dashboard is the unconditional alias and
              always renders the teacher Dashboard, even for a parent
              previewing the URL directly.
            */}
            <Route path="/dashboard" element={
              <PrivateRoute>
                {isParent ? <MasteryMap /> : <Dashboard />}
              </PrivateRoute>
            } />
            <Route path="/teacher/dashboard" element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            } />
            {/* Phase 4 (core learning loop) - parent-only routes, reachable
                only via links from MasteryMap once a learner exists. */}
            <Route path="/dashboard/placement/:learnerId" element={
              <PrivateRoute>
                <PlacementFlow />
              </PrivateRoute>
            } />
            <Route path="/dashboard/skills/:skillId" element={
              <PrivateRoute>
                <SkillDetail />
              </PrivateRoute>
            } />
            <Route path="/dashboard/skill-tree" element={
              <PrivateRoute>
                <SkillTree />
              </PrivateRoute>
            } />
            {/* Parent-initiated assessments hub - reachable from the nav any
                time, not just as a one-time wall right after adding a child
                (see MasteryMap's dismissible prompt for that softer nudge). */}
            <Route path="/dashboard/assessments" element={
              <PrivateRoute>
                <AssessmentsHub />
              </PrivateRoute>
            } />
            {/* Phase 6 (parent progress view) - single-child MVP, matching
                MasteryMap's own no-param convention (learner resolved via
                FamilyContext). */}
            <Route path="/dashboard/progress" element={
              <PrivateRoute>
                <WeeklyProgress />
              </PrivateRoute>
            } />
            <Route path="/subscription" element={
              <PrivateRoute>
                {withInteriorLayout(Subscription)({})}
              </PrivateRoute>
            } />
            <Route path="/subscription/success" element={
              <PrivateRoute>
                {withInteriorLayout(SubscriptionSuccess)({})}
              </PrivateRoute>
            } />
            <Route path="/subscription/failure" element={
              <PrivateRoute>
                {withInteriorLayout(SubscriptionFailure)({})}
              </PrivateRoute>
            } />
            <Route path="/guided-reading" element={
              <PrivateRoute>
                {withInteriorLayout(GuidedReading)({})}
              </PrivateRoute>
            } />
            <Route path="/intervention" element={
              <PrivateRoute>
                {withInteriorLayout(Intervention)({})}
              </PrivateRoute>
            } />
            <Route path="/students" element={
              <PrivateRoute>
                {withInteriorLayout(Students)({})}
              </PrivateRoute>
            } />
            <Route path="/student-list" element={
              <PrivateRoute>
                {withInteriorLayout(StudentList)({})}
              </PrivateRoute>
            } />
            <Route path="/manage-students" element={
              <PrivateRoute>
                {withInteriorLayout(StudentList)({})}
              </PrivateRoute>
            } />
            <Route path="/add-students" element={
              <PrivateRoute>
                {withInteriorLayout(AddStudents)({})}
              </PrivateRoute>
            } />
            {/* Redirect old routes to the new unified groups page */}
            <Route path="/groups" element={<Navigate to="/add-group" replace />} />
            <Route path="/group-list" element={<Navigate to="/add-group" replace />} />
            <Route path="/add-group" element={
              <PrivateRoute>
                {withInteriorLayout(GroupList)({})}
              </PrivateRoute>
            } />
            <Route path="/settings" element={
              <PrivateRoute>
                {withInteriorLayout(Settings)({})}
              </PrivateRoute>
            } />
            <Route path="/passages" element={
              <PrivateRoute>
                {withInteriorLayout(Passages)({})}
              </PrivateRoute>
            } />
            <Route path="/passage-list" element={
              <PrivateRoute>
                {withInteriorLayout(PassagesList)({})}
              </PrivateRoute>
            } />
            <Route path="/short-passages" element={
              <PrivateRoute>
                {withInteriorLayout(ShortPassages)({})}
              </PrivateRoute>
            } />
            <Route path="/worksheets" element={
              <PrivateRoute>
                {withInteriorLayout(Worksheets)({})}
              </PrivateRoute>
            } />
            <Route path="/assessments" element={
              <PrivateRoute>
                {withInteriorLayout(Assessments)({})}
              </PrivateRoute>
            } />
            <Route path="/math-word-problems" element={
              <PrivateRoute>
                {withInteriorLayout(MathWordProblems)({})}
              </PrivateRoute>
            } />
            <Route path="/math-word-problems-list" element={
              <PrivateRoute>
                {withInteriorLayout(MathWordProblemsList)({})}
              </PrivateRoute>
            } />
            <Route path="/tools" element={
              <PrivateRoute>
                {withInteriorLayout(Tools)({})}
              </PrivateRoute>
            } />
            <Route path="/small-group/lesson-plan" element={
              <PrivateRoute>
                {withInteriorLayout(SmallGroupLessonPlan)({})}
              </PrivateRoute>
            } />
            <Route path="/small-group/saved" element={
              <PrivateRoute>
                {withInteriorLayout(SavedLessonPlans)({})}
              </PrivateRoute>
            } />
            <Route path="/small-group/delivery" element={
              <PrivateRoute>
                <LessonDelivery />
              </PrivateRoute>
            } />
            <Route path="/small-group/delivered-lessons" element={
              <PrivateRoute>
                {withInteriorLayout(DeliveredLessons)({})}
              </PrivateRoute>
            } />
            <Route path="/small-group/delivery/:id" element={
              <PrivateRoute>
                <LessonDelivery />
              </PrivateRoute>
            } />
            <Route path="/ai-chat" element={
              <PrivateRoute>
                {withInteriorLayout(AIChat)({})}
              </PrivateRoute>
            } />
            <Route path="/edu-chat" element={
              <PrivateRoute>
                <TeachingChat />
              </PrivateRoute>
            } />
            <Route path="/teacher/edu-chat" element={
              <PrivateRoute>
                <TeachingChat />
              </PrivateRoute>
            } />
            <Route path="/behavior" element={
              <PrivateRoute>
                {withInteriorLayout(BehaviorPageEnhanced)({})}
              </PrivateRoute>
            } />
            <Route path="/teacher/behavior" element={
              <PrivateRoute>
                {withInteriorLayout(BehaviorPageEnhanced)({})}
              </PrivateRoute>
            } />
            <Route path="/instruction" element={
              <PrivateRoute>
                {withInteriorLayout(InstructionPage)({})}
              </PrivateRoute>
            } />
            <Route path="/teacher/instruction" element={
              <PrivateRoute>
                {withInteriorLayout(InstructionPage)({})}
              </PrivateRoute>
            } />
            <Route path="/test-builder" element={
              <PrivateRoute>
                {withInteriorLayout(TestBuilder)({})}
              </PrivateRoute>
            } />
            <Route path="/grade-tests" element={
              <PrivateRoute>
                {withInteriorLayout(GradeTests)({})}
              </PrivateRoute>
            } />
            <Route path="/grade-submissions" element={
              <PrivateRoute>
                {withInteriorLayout(GradeSubmissions)({})}
              </PrivateRoute>
            } />
            <Route path="/tests" element={
              <PrivateRoute>
                {withInteriorLayout(GradeTests)({})}
              </PrivateRoute>
            } />
            <Route path="/tests/create" element={
              <PrivateRoute>
                {withInteriorLayout(TestBuilder)({})}
              </PrivateRoute>
            } />
            <Route path="/tests/grade" element={
              <PrivateRoute>
                {withInteriorLayout(GradeTests)({})}
              </PrivateRoute>
            } />
            <Route path="/tests/edit/:testId" element={
              <PrivateRoute>
                {withInteriorLayout(TestBuilder)({})}
              </PrivateRoute>
            } />
            <Route path="/tests/grade/:testId" element={
              <PrivateRoute>
                {withInteriorLayout(GradeSubmissions)({})}
              </PrivateRoute>
            } />
            <Route path="/gradebook" element={
              <PrivateRoute>
                {withInteriorLayout(Gradebook)({})}
              </PrivateRoute>
            } />
            <Route path="/take-test/:testId" element={
              <PrivateRoute>
                <TakeTest />
              </PrivateRoute>
            } />
            <Route path="/test/:testId" element={
              <TakeTest />
            } />
            <Route path="/complete-profile" element={
              <PrivateRoute>
                <CompleteProfile />
              </PrivateRoute>
            } />
            <Route path="/profile" element={
              <PrivateRoute>
                {withInteriorLayout(Profile)({})}
              </PrivateRoute>
            } />
            <Route path="/admin/teacher-stats" element={
              <PrivateRoute>
                {withInteriorLayout(TeacherStats)({})}
              </PrivateRoute>
            } />
            <Route path="/standards-visualization" element={
              <PrivateRoute>
                {withInteriorLayout(StandardsVisualization)({})}
              </PrivateRoute>
            } />
            </Routes>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App;
